import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { observer } from "mobx-react-lite";

import {
  BreadcrumbComponent,
  ContentComponent,
  ContentSidebarComponent,
  FooterComponent,
  NavbarComponent,
  OverlaySidebarComponent,
} from "..";
import { RouteConstants } from "../../../models/enums/route-constants.enum";
import { IMenuItem } from "../../../models/interfaces";
import { useStore } from "../../../stores/StoreContext";
import { GlobalErrorMessages, SidebarToggleButton } from "../../Shared";
import { GlobalPrimeReactDefinitions } from "../../Shared/GlobalPrimeReactDefinitions/GlobalPrimeReactDefinitions";

import "./Main.scss";

import { AiUsageWarning } from "../../Shared/Components";

const RawMainComponent: FunctionComponent = () => {
  const { sidebarStore, breakpointStore, navigationStore } = useStore();
  const location = useLocation();
  const [hasSidebar, setHasSidebar] = useState(false);
  const shouldShowAiWarning =
    location.pathname.startsWith(`/${RouteConstants.LandingPage}`) ||
    location.pathname.startsWith(`/${RouteConstants.Ai}`);

  const storeContext = useStore();

  const getSideBarComponent = (): ReactNode => {
    return navigationStore.currentActiveMenuItem?.sidebarComponent?.();
  };

  const checkHasSidebar = async (menuItem: IMenuItem) => {
    let hasSidebar = false;

    if (menuItem.hasSideBar instanceof Function) {
      const result = menuItem.hasSideBar(storeContext);
      hasSidebar = result ?? false;
    } else {
      hasSidebar = menuItem.hasSideBar ?? false;
    }

    setHasSidebar(hasSidebar);
  };

  useEffect(() => {
    if (!navigationStore.currentActiveMenuItem) {
      return;
    }

    checkHasSidebar(navigationStore.currentActiveMenuItem);
  }, [navigationStore.currentActiveMenuItem]);

  return (
    <div className="page-container">
      <nav className="navbar-area">
        <NavbarComponent />
      </nav>

      <div className="breadcrump-area">
        <BreadcrumbComponent />
      </div>

      <section className="main-area">
        <aside className="sidebar-container">
          {hasSidebar && (
            <SidebarToggleButton
              onClick={() => {
                if (sidebarStore.isOverlaySidebar(breakpointStore.device)) {
                  sidebarStore.setOverlaySidebarVisible(true);
                  return;
                }
                sidebarStore.toggleCollapsed();
              }}
              customStyles={{ paddingLeft: "1rem", paddingRight: "1rem" }}
            />
          )}

          {hasSidebar === true &&
            (sidebarStore.isOverlaySidebar(breakpointStore.device) ? (
              <OverlaySidebarComponent>{getSideBarComponent()}</OverlaySidebarComponent>
            ) : (
              <ContentSidebarComponent>{getSideBarComponent()}</ContentSidebarComponent>
            ))}
        </aside>

        <main className="main-container">
          <div className="content-area">
            <GlobalErrorMessages>
              {content => {
                return <div className="global-errors-container">{content}</div>;
              }}
            </GlobalErrorMessages>

            <GlobalPrimeReactDefinitions />

            <ContentComponent />

            {shouldShowAiWarning && <AiUsageWarning type="aiWarning" />}
          </div>
        </main>
      </section>

      {navigationStore.currentActiveMenuItem?.showFooter && (
        <footer className="footer-area">
          <FooterComponent />
        </footer>
      )}
    </div>
  );
};

export const MainComponent = observer(RawMainComponent);
